/**
 * The vendor's own actions (phase 7b): who created, suspended or reactivated
 * a tenant, set or cleared a pin, wrote an announcement — newest first, a
 * page at a time ("Load more").
 */
import type { VendorActivity } from '@acp/api-contract/vendor.js';
import { Button } from '../ui/button.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type ActivityLogProps = {
  readonly entries: readonly VendorActivity[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly hasMore: boolean;
  readonly onMore: () => void;
};

export function ActivityLog({ entries, loading, error, hasMore, onMore }: ActivityLogProps) {
  return (
    <div className="grid gap-3" data-testid="activity-log">
      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          Could not load the activity: {error}
        </p>
      )}
      {!loading && error === null && entries.length === 0 ? <p className="py-4 text-sm text-muted-foreground">No vendor actions recorded yet.</p> : null}
      {entries.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Who</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Target</TableHead>
              <TableHead>Detail</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.map((e) => (
              <TableRow key={e.activityId} data-testid="activity-row">
                <TableCell className="whitespace-nowrap text-sm">{e.at.slice(0, 19).replace('T', ' ')}</TableCell>
                <TableCell className="text-sm">{e.actor.email}</TableCell>
                <TableCell className="font-mono text-xs">{e.action}</TableCell>
                <TableCell className="font-mono text-xs">{e.target}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{e.detail}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
      {hasMore && !loading ? (
        <Button variant="outline" size="sm" className="w-fit" onClick={onMore} data-testid="activity-more">
          Load more
        </Button>
      ) : null}
    </div>
  );
}
